import React, { useState, useEffect, useMemo } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Autocomplete, TextField, Box, Button } from "@mui/material";
import { BankingSyndicate, Organization, useDealContext, Contact } from "@/contexts/DealContext";
import { useParams } from "react-router";
import { Close } from "@mui/icons-material";
import theme from "@/theme";

type ContactReviewProps = {
  Contacts: Contact[];
  onChange: (updatedContacts: Contact[]) => void;
};

const ContactReview: React.FC<ContactReviewProps> = ({ Contacts, onChange }) => {
  const [rows, setRows] = useState<Contact[]>(Contacts);
  useEffect(() => {
    onChange(rows);
  }, [rows]);

  const columns: GridColDef[] = [
    {
      field: "firstname",
      headerName: "Firstname",
      flex: 1,
      editable: true,
    },
    {
      field: "lastname",
      headerName: "Lastname",
      flex: 1,
      editable: true,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      editable: true,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      editable: true,
      renderEditCell: (params) => {
        return (
          <Autocomplete
            sx={{ width: '100%' }}
            options={["borrower", "lender", "agent"]}
            value={params.value || null}
            onChange={(_, newValue) => {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: newValue || "",
              });
            }}
            disableClearable
            renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
          />
        );
      },
    },
    {
      field: "organization",
      headerName: "Organization",
      flex: 1,
      editable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams) => (
        <Box
          component="span"
          sx={{
            display: "flex", // 👈 ensures flexbox
            alignItems: "center", // 👈 vertical centering
            justifyContent: "center", // 👈 horizontal centering
            cursor: "pointer",
            color: "error.main",
            height: "100%",
          }}
          onClick={() => {
            setRows((prev) => prev.filter((_, index) => index !== params.row.id));
          }}
        >
          <Close sx={{ fontSize: "1rem" }} />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <DataGrid
        rows={rows.map((row, index) => ({ id: index, ...row }))}
        columns={columns}
        disableRowSelectionOnClick
        hideFooter
        density="compact"
        autoHeight
        processRowUpdate={(newRow: { id: number }) => {
          const updatedRow: Contact = newRow as Contact;
          setRows((prev) => prev.map((row, index) => (index === newRow.id ? updatedRow : row)));
          return { ...newRow };
        }}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <Button
          color="info"
          onClick={() =>
            setRows((prev) => [
              ...prev,
              {
                id: prev.length,
                name: "",
                email: "",
                role: "",
                organization: "",
              },
            ])
          }
        >
          Add Contact
        </Button>
      </Box>
    </Box>
  );
};

export default ContactReview;
